import type { LocationItem } from '#src/app/pages/search/shared/types';
import { removeDiacritics } from '#src/app/shared/utils/string';

/**
 * Internal to the location search hooks: tier is ranking state, stripped before items reach the UI
 */
export type RankedLocationItem = LocationItem & {
	// how well the item matches the typed query (see getMatchTier), lower is better
	tier?: number;
};

// users often pause after this many characters, where a city prefix ("la" -> Los Angeles) is the likelier intent
const SHORT_QUERY_MAX_LENGTH = 2;

export function normalizeLocationText(value: string): string {
	return removeDiacritics(value.toLowerCase()).trim();
}

/**
 * Lower is better: 0 exact, 1 prefix, 2 word prefix ("tanzania, united republic of"), 3 substring, 4 anything else (fuzzy)
 */
export function getMatchTier(name: string, normalizedQuery: string): number {
	const normalizedName = normalizeLocationText(name);
	if (normalizedName === normalizedQuery) return 0;
	if (normalizedName.startsWith(normalizedQuery)) return 1;
	if (normalizedName.split(/[\s,()'-]+/).some((word) => word.startsWith(normalizedQuery))) return 2;
	if (normalizedName.includes(normalizedQuery)) return 3;
	return 4;
}

type MergeOptions = { query: string; maxItems: number; maxCountries: number };

/**
 * Sorts by match tier, then keeps each list's own order. Countries come first on ties as they are broader,
 * except for short queries where cities do
 */
export function mergeLocationSuggestions(
	cities: readonly RankedLocationItem[],
	countries: readonly RankedLocationItem[],
	{ query, maxItems, maxCountries }: MergeOptions,
): readonly LocationItem[] {
	const cappedCountries = countries.slice(0, maxCountries);
	const isShortQuery = normalizeLocationText(query).length <= SHORT_QUERY_MAX_LENGTH;

	return (isShortQuery ? [...cities, ...cappedCountries] : [...cappedCountries, ...cities])
		.map((item, index) => ({ item, index }))
		.sort((a, b) => (a.item.tier ?? 0) - (b.item.tier ?? 0) || a.index - b.index)
		.slice(0, maxItems)
		.map(({ item: { tier: _tier, ...item } }) => item);
}
